import ErrorMessage from '@/components/ErrorMessage'
import GsuLoader from '@/components/GsuLoader'
import AboutBlock, { Props as AboutBlockProps } from '@/compositions/AboutBlock'
import AccessMessageBlock from '@/compositions/AccessMessageBlock'
import AttendedSessionsBlock from '@/compositions/AttendedSessionBlock'
import BookMultipleSessionsBlock from '@/compositions/BookMultipleSessionsBlock'
import CardBlock from '@/compositions/CardBlock'
import CategoryClassesBlock from '@/compositions/CategoryClassesBlock'
import CategoryIndexBlock from '@/compositions/CategoryIndexBlock'
import CategoryInfoBlock from '@/compositions/CategoryInfoBlock'
import ClassCardRow from '@/compositions/ClassCardRow'
import ClassCtaBlock from '@/compositions/ClassCtaBlock'
import ClassSummaryBlock from '@/compositions/ClassSummaryBlock'
import ContentBlock from '@/compositions/ContentBlock'
import CtaBlock from '@/compositions/CtaBlock'
import FeaturedClassesBlock from '@/compositions/FeaturedClassesBlock'
import GoogleAdBannerBlock from '@/compositions/GoogleAdBannerBlock'
import GuideBlock from '@/compositions/GuideBlock'
import GuideClassesBlock from '@/compositions/GuideClassesBlock'
import GuideIndexInfoBlock from '@/compositions/GuideIndexInfoBlock'
import GuidePageInfoBlock from '@/compositions/GuidePageInfoBlock'
import GuidePageSectionBlock from '@/compositions/GuidePageSectionBlock'
import GuideSessionsBlock from '@/compositions/GuideSessionsBlock'
import HelenSearchBlock from '@/compositions/HelenSearchBlock'
import HelpInfoBlock, { Props as HelpInfoBlockProps } from '@/compositions/HelpInfoBlock'
import HeroInfoBlock from '@/compositions/HeroInfoBlock'
import ImageBlock from '@/compositions/ImageBlock'
import LiveGuidesBlock from '@/compositions/LiveGuidesBlock'
import MicroShortBlock from '@/compositions/MicroShortBlock'
import NotesDetailsBlock from '@/compositions/NotesDetailsBlock'
import NotesInfoBlock from '@/compositions/NotesInfoBlock'
import PartnerInfoBlock from '@/compositions/PartnerInfoBlock'
import PartnershipBannerBlock from '@/compositions/PartnershipBannerBlock'
import PopularCategoryBlock from '@/compositions/PopularCategoryBlock'
import PopularSeriesBlock from '@/compositions/PopularSeriesBlock'
import PricingBlock from '@/compositions/PricingBlock'
import ReviewBlock from '@/compositions/ReviewBlock'
import ScheduleBlock from '@/compositions/ScheduleBlock'
import ScheduleBlockMobile from '@/compositions/ScheduleBlock/ScheduleBlockMobile'
import SectionGroupBlock from '@/compositions/SectionGroupBlock'
import SeriesBlock from '@/compositions/SeriesBlock'
import SeriesClassesBlock from '@/compositions/SeriesClassesBlock'
import SeriesClassesRowBlock from '@/compositions/SeriesClassesRowBlock'
import SeriesIndexBlock from '@/compositions/SeriesIndexInfoBlock'
import SeriesInfoBlock from '@/compositions/SeriesInfoBlock'
import SnippetBlock, { Props as SnippetBlockProps } from '@/compositions/SnippetBlock'
import SponsorInfoBlock from '@/compositions/SponsorInfoBlock'
import TestimonialBlock, { Props as TestimonialBlockProps } from '@/compositions/TestimonialBlock'
import UpcomingSessionsBlock from '@/compositions/UpcomingSessionsBlock'
import {
  ClassSummaryPageSection,
  CompanyInfoPageSection,
  CtaPageSection,
  GoogleAdBannerSection,
  HelpInfoPageSection,
  ImagePageSection,
  MicroLearningShortsSection,
  PageSectionUnion,
  PartnershipBannerSection,
  SectionGroupLayoutHint,
  SnippetPageSection,
  SponsorInfoPageSection,
  TestimonialsPageSection,
} from '@/shared/api-generated-types'
import { Tab } from '@headlessui/react'
import { CombinedError } from 'urql'

type Sections = Array<PageSectionUnion | undefined>
type LayoutHint = SectionGroupLayoutHint

export interface Props {
  fetching?: boolean
  error?: CombinedError | undefined
  /**
   * Feed a className to specific sections to help with contextual
   * styling when those sections are used in certain pages or arrangements
   * of multiple PageComposer that utilise include/exclude sections
   */
  sectionClassNames?: { [key: string]: string }
  /**
   * Load callback passes page number to load, and the section
   * index which may be helpful for parent state management
   */
  onLoadMoreByPageNumber?: (idx: number, page: number) => void
  /**
   * Loading callback that loads the next batch of results from the api
   * @param nextPageToken - The token of the next page of results, as returned by the api.
   */
  onLoadMoreByNextPageToken?: (nextPageToken: string) => void
  /**
   * Only output specific sections for this instance of PageComposer
   */
  includeSections?: string[]
  /**
   * Output all sections except for these ones for this instance of PageComposer
   * If both are set, excludeSections is favoured
   */
  excludeSections?: string[]
  sections?: Sections
  /**
   * Helps inform sections of the any filter change, mostly used to
   * help purge old results if that's a concern for them
   */
  distinction?: string
  partnerId?: string
}

/**
 * Purely an ergonomic method for generating unique keys, given we may have
 * multiple sectionId used in a single Page composition, inclusive of recursion
 */
const getSectionKey = (sectionId: string, idx: number): string => `${idx}:${sectionId}`

/**
 * RenderSection is a small SFC that's responsible for wrapping
 * each section in the appropriate layout component depending
 * on what layoutHint is used
 */
const RenderSection = ({
  layoutHint,
  children,
}: {
  layoutHint?: LayoutHint
  children: JSX.Element | null
}): JSX.Element | null => {
  if (layoutHint === SectionGroupLayoutHint.Tab) {
    return <Tab.Panel>{children}</Tab.Panel>
  } else {
    return <>{children}</>
  }
}

/**
 * composeSections - Core method to translate Page data into React
 * page sections (block compositions). Seperate method to assist
 * with recursion when hitting `SectionGroup` blocks.
 *
 * NB. The use of Omit and renaming certain keys is due to PageSectionUnion
 * containing a few variants of `content`, `items` and similar, which GraphQL
 * doesn't support, and thus requires aliases against those response keys.
 * The response keys therefore do not match the expected section definitions
 * that are checked here, so we reshape the type, and pass the aliased keys
 */
const _composeSections = (
  {
    sections,
    onLoadMoreByPageNumber,
    onLoadMoreByNextPageToken,
    includeSections,
    excludeSections,
    fetching,
    sectionClassNames,
    distinction,
    partnerId,
    error,
  }: Partial<Props>,
  layoutHint?: LayoutHint,
): JSX.Element | null => {
  // If the GraphQL request has finished but we get no sections, we've hit an issue.
  if (!fetching && error) return <ErrorMessage />

  if (!sections) return null

  return (
    <>
      {sections.map((section, idx: number) => {
        let currentSection: JSX.Element | null = null
        let sectionLayoutHint: LayoutHint | undefined = undefined
        const getClassName = (): string => (sectionClassNames ? sectionClassNames[section?.__typename ?? ''] : '')
        let maybeLoadMore: (() => void) | undefined
        if (!section?.__typename) return null
        if (excludeSections && excludeSections.includes(section?.__typename)) return null
        else if (includeSections && !includeSections.includes(section?.__typename)) return null

        if ('pageInfo' in section) {
          // Default to the onLoadMore function that uses next page tokens because that is more robust.
          // But if that function is not supplied by the caller, fallback to the old page numbers method.

          if (section?.pageInfo && onLoadMoreByNextPageToken) {
            maybeLoadMore = () => {
              onLoadMoreByNextPageToken(section.pageInfo.nextPageCursor ?? '')
            }
          } else if (section?.pageInfo && onLoadMoreByPageNumber) {
            maybeLoadMore = () => {
              onLoadMoreByPageNumber(idx, section.pageInfo.page ?? 0)
            }
          } else {
            maybeLoadMore = () => {}
          }
        }

        switch (section?.__typename) {
          case 'AttendedSessionsPageSection':
            currentSection = <AttendedSessionsBlock {...section} onLoadMore={maybeLoadMore} fetching={fetching} />
            break
          case 'SearchPageSection':
          case 'SimilarClassesPageSection': {
            currentSection = (
              <CardBlock
                className={getClassName()}
                fetching={fetching}
                items={section?.items}
                onLoadMore={maybeLoadMore}
                pageInfo={section?.pageInfo}
                subtitle={section?.subtitle}
                title={section?.title}
                spacing="sm"
                carousel={section?.__typename !== 'SearchPageSection' ? ['lg', 'sm', 'md'] : false}
                navigationActions={section?.__typename !== 'SearchPageSection' ? section?.navigationActions : undefined}
              />
            )
            break
          }

          case 'LiveAndUpcomingClassesPageSection':
          case 'FeaturedSessionsSection':
          case 'CategorySessionsSection':
          case 'MostPopularPageSection': {
            currentSection = (
              <ClassCardRow
                fetching={fetching}
                className={getClassName()}
                items={section?.items}
                subtitle={section?.subtitle}
                title={section?.title}
                spacing="sm"
                carousel={['lg', 'sm', 'md']}
                navigationActions={section?.navigationActions}
              />
            )
            break
          }

          case 'SchedulePageSection': {
            const width = window?.innerWidth
            const url = window.location.href
            if (width < 601 && url.includes('schedule')) {
              currentSection = (
                <ScheduleBlockMobile
                  className={getClassName()}
                  fetching={fetching}
                  items={section?.items}
                  onLoadMore={maybeLoadMore}
                  pageInfo={section?.pageInfo}
                  distinction={distinction}
                />
              )
            } else {
              currentSection = (
                <ScheduleBlock
                  className={getClassName()}
                  fetching={fetching}
                  items={section?.items}
                  onLoadMore={maybeLoadMore}
                  pageInfo={section?.pageInfo}
                  distinction={distinction}
                />
              )
            }
            break
          }
          case 'UpcomingPageSection':
            currentSection = <UpcomingSessionsBlock {...section} className={getClassName()} />
            break

          case 'NextSessionSection':
            currentSection = <ClassCtaBlock noCancel classId={section?.classId} session={section.items} />
            break

          case 'PartnerInfoPageSection':
            currentSection = <PartnerInfoBlock {...section.content} className={getClassName()} />
            break

          case 'SeriesIndexInfoPageSection':
            currentSection = <SeriesIndexBlock {...section} />
            break

          case 'FeaturedClassesSection':
            currentSection = <FeaturedClassesBlock {...section} />
            break

          case 'SeriesInfoPageSection':
            currentSection = <SeriesInfoBlock {...section.content} className={getClassName()} />
            break

          case 'SeriesPageSection':
            currentSection = <SeriesBlock {...section} />
            break

          case 'PopularSeriesSection':
            currentSection = <PopularSeriesBlock {...section} />
            break

          case 'PopularCategoriesSection':
            currentSection = <PopularCategoryBlock {...section} />
            break

          case 'CategoryIndexPageSection':
            currentSection = <CategoryIndexBlock {...section} />
            break

          case 'CategoryInfoSection':
            currentSection = <CategoryInfoBlock {...section} />
            break

          case 'CategoryClassesSection':
            currentSection = <CategoryClassesBlock {...section} fetching={fetching} onLoadMore={maybeLoadMore} />
            break

          case 'LiveGuidesSection':
            currentSection = <LiveGuidesBlock {...section} />
            break

          case 'SeriesClassesPageSection':
            currentSection = <SeriesClassesBlock {...section} />
            break

          case 'SeriesClassesRowSection':
            currentSection = <SeriesClassesRowBlock {...section} />
            break

          case 'PricingPageSection':
            currentSection = <PricingBlock {...section} />
            break

          case 'BookMultipleSessionsSection':
            currentSection = <BookMultipleSessionsBlock {...section} />
            break

          case 'SnippetPageSection': {
            const thisSection: Omit<SnippetPageSection, 'items'> & {
              snippetItems: SnippetPageSection['items']
            } = {
              snippetItems: section.items,
              ...section,
            }
            currentSection = (
              <SnippetBlock items={thisSection.snippetItems as SnippetBlockProps['items']} title={thisSection.title} />
            )
            break
          }

          case 'HelpInfoPageSection': {
            const thisSection: Omit<HelpInfoPageSection, 'items'> & {
              helpInfoItems: HelpInfoPageSection['items']
            } = {
              helpInfoItems: section.items,
              ...section,
            }
            currentSection = (
              <HelpInfoBlock
                items={thisSection.helpInfoItems as HelpInfoBlockProps['items']}
                title={thisSection.title}
              />
            )
            break
          }

          case 'TestimonialsPageSection': {
            const thisSection: Omit<TestimonialsPageSection, 'items'> & {
              quoteItems: TestimonialsPageSection['items']
            } = {
              quoteItems: section.items,
              ...section,
            }
            currentSection = (
              <TestimonialBlock
                quoteItems={thisSection.quoteItems as TestimonialBlockProps['quoteItems']}
                title={thisSection.title}
                className={getClassName()}
              />
            )
            break
          }

          case 'CompanyInfoPageSection': {
            const thisSection: Omit<CompanyInfoPageSection, 'items'> & { infoItems: CompanyInfoPageSection['items'] } =
              {
                infoItems: section.items,
                ...section,
              }
            currentSection = (
              <AboutBlock
                companyInfoItems={thisSection.infoItems as AboutBlockProps['companyInfoItems']}
                title={thisSection.title}
                subtitle={thisSection.subtitle}
                className={getClassName()}
              />
            )
            break
          }

          case 'ContentPageSection':
            currentSection = <ContentBlock {...section} className={getClassName()} />
            break

          case 'SponsorInfoPageSection': {
            const thisSection: Omit<SponsorInfoPageSection, 'content'> & {
              infoContent: SponsorInfoPageSection['content']
            } = {
              infoContent: section.content,
              ...section,
            }
            currentSection = (
              <SponsorInfoBlock sponsorInfoContent={thisSection.infoContent} className={getClassName()} />
            )

            break
          }
          case 'ImagePageSection': {
            const thisSection: Omit<ImagePageSection, 'items'> & { imageItems: ImagePageSection['items'] } = {
              imageItems: section.items,
              ...section,
            }
            currentSection = (
              <ImageBlock
                minWidth
                items={thisSection.imageItems}
                accessDisplay={thisSection.accessDisplay}
                isCommunityLedClass={thisSection.isCommunityLedClass}
                className={getClassName()}
              />
            )
            break
          }

          case 'ReviewPageSection':
            currentSection = <ReviewBlock {...section} className={getClassName()} />
            break

          case 'CTAPageSection':
            const thisSection: CtaPageSection = { ...section }
            currentSection = <CtaBlock {...thisSection} className={getClassName()} />
            break

          case 'GuideClassPageSection':
            currentSection = <GuideBlock {...section} className={getClassName()} />
            break

          case 'ClassSummaryPageSection': {
            const thisSection: Omit<ClassSummaryPageSection, 'content'> & {
              classSummaryContent: ClassSummaryPageSection['content']
            } = {
              classSummaryContent: section.content,
              ...section,
            }
            currentSection = <ClassSummaryBlock content={thisSection.classSummaryContent} className={getClassName()} />
            break
          }

          case 'HeroInfoSection':
            currentSection = <HeroInfoBlock {...section} className={getClassName()} />
            break

          case 'AccessMessageSection':
            currentSection = <AccessMessageBlock {...section} className={getClassName()} />
            break

          case 'MicroLearningShortsSection':
            const microLearningShortsSection: Omit<MicroLearningShortsSection, 'items'> & {
              videoItems: MicroLearningShortsSection['items']
            } = {
              videoItems: section.items,
              ...section,
            }
            currentSection = (
              <MicroShortBlock {...section} items={microLearningShortsSection.videoItems} className={getClassName()} />
            )
            break

          case 'GuideIndexInfoPageSection':
            currentSection = <GuideIndexInfoBlock {...section} />
            break

          case 'GuideInfoPageSection':
            currentSection = <GuidePageInfoBlock {...section.content} className={getClassName()} />
            break

          case 'GuidePageSection':
            currentSection = <GuidePageSectionBlock {...section} />
            break

          case 'GuideClassesPageSection':
            currentSection = <GuideClassesBlock {...section} />
            break

          case 'GuideSessionsPageSection':
            currentSection = <GuideSessionsBlock {...section} />
            break

          // case 'FeaturedClassesSection': {
          //   currentSection = <p>TODO FeaturedClassesSection</p>
          //   break
          // }

          case 'SessionNotesInfoPageSection':
            currentSection = <NotesInfoBlock {...section} />
            break

          case 'SessionNotesDetailsPageSection':
            currentSection = <NotesDetailsBlock {...section} />
            break

          /* Recursively call renderSections on nested sections */
          case 'SectionGroupPageSection': {
            const { sections: groupSections } = section
            sectionLayoutHint = section.layoutHint
            // Ah check to remove tabs if the data returned from backend
            // does not include guide information.
            // This removes the tabs if the above scenario is true
            if (section.sections.length === 1) {
              sectionLayoutHint = SectionGroupLayoutHint.Stack
            }
            currentSection = (
              <SectionGroupBlock
                key={getSectionKey(section?.id, idx)}
                sections={section?.sections}
                layoutHint={sectionLayoutHint}
                className={getClassName()}
              >
                {composeSections(
                  {
                    sections: groupSections,
                    fetching,
                    onLoadMoreByPageNumber: onLoadMoreByPageNumber,
                    includeSections,
                    excludeSections,
                    sectionClassNames,
                  },
                  sectionLayoutHint,
                )}
              </SectionGroupBlock>
            )
            break
          }

          case 'PartnershipBannerSection': {
            const thisSection: Omit<PartnershipBannerSection, 'content'> & {
              partnershipBannerContent: PartnershipBannerSection['content']
            } = {
              partnershipBannerContent: section.content,
              ...section,
            }
            currentSection = (
              <PartnershipBannerBlock
                id={getSectionKey(section?.id, idx)}
                content={thisSection.partnershipBannerContent}
              />
            )
            break
          }

          case 'GoogleAdBannerSection': {
            const thisSection: Omit<GoogleAdBannerSection, 'content'> & {
              googleAdBannerContent: GoogleAdBannerSection['content']
            } = {
              googleAdBannerContent: section.content,
              ...section,
            }
            currentSection = (
              <GoogleAdBannerBlock id={getSectionKey(section?.id, idx)} content={thisSection.googleAdBannerContent} />
            )
            break
          }

          case 'HelenSearchSection':
            currentSection = <HelenSearchBlock id={getSectionKey(section?.id, idx)} partnerId={partnerId} />
            break

          default:
            // Just ignore anything we don't know how to render (yet). This is to allow adding
            // new types to the backend without breaking clients.
            currentSection = <></>
        }

        return (
          <RenderSection key={getSectionKey(section?.id, idx)} layoutHint={layoutHint}>
            {currentSection}
          </RenderSection>
        )
      })}
    </>
  )
}

// Memoize the section rendering to avoid any potential re-rendering
// Would suggest keeping an eye on this, mostly implemented as premature
// optimisation knowing how heavy the iteration _could_ be as it grows
const composeSections = _composeSections

const PageComposer = ({
  sections,
  fetching,
  error,
  onLoadMoreByPageNumber,
  onLoadMoreByNextPageToken,
  includeSections,
  excludeSections,
  sectionClassNames,
  distinction,
  partnerId,
}: Props): JSX.Element | null => {
  const getSections = composeSections
  if (fetching && !sections) return <GsuLoader consumeSpace />
  return getSections({
    sections,
    onLoadMoreByPageNumber,
    onLoadMoreByNextPageToken,
    includeSections,
    excludeSections,
    sectionClassNames,
    fetching,
    distinction,
    partnerId,
    error,
  })
}

export default PageComposer
